import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericResModel, ProceduresNotesModel, ProceduresNotesReqModel } from 'countable@model';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { TbAccountReqModel } from '../model/tb/account/tb-account-req.model';
import { TbAccountResModel } from '../model/tb/account/tb-account-res.model';
import { JournalReqModel } from '../model/tb/journal/journal-req.model';
import { JournalResModel } from '../model/tb/journal/journal-res.model';
import { StatementViewTypeModel } from '../model/tb/statement-view-type.model';
import { ZeroAccounts, ZeroAccountsModel } from '../model/tb/tb-zero-import.model';
import { deleteTbAccReqModel, editRequestModel, TrialBalanceModel } from '../model/tb/trial-balance.model';
import { UngroupModel } from '../model/tb/ungroup.model';

@Injectable({
  providedIn: 'root'
})
export class TbApiService {

  private static readonly tbBaseUri = environment.apiV1 + '/tb';
  private static readonly multiMapping = environment.apiV1 + '/tb/multi-mapping';
  private static readonly leadSheetCode = '/leadsheet-code/';
  private readonly TB_ACC_DETAILS_COUNT = environment.apiV1 + '/trial-balance-delete-accounts'
  private readonly TB_EDIT_ACC = '/account/update'
  private static readonly TB_VALIDATE_ACCNO_DESC = TbApiService.tbBaseUri + '/account/validate-name-code';
  private static readonly SHORT_CUT_BUTTON = environment.apiV1 + '/shortcut-button/';
  private static readonly TB_ACCOUNT = environment.apiV1 + '/trial-balance-account';
  private static readonly UNGROUP = environment.apiV1 + '/ungroup';
  private static readonly UNMAP = environment.apiV1 + '/unmap';
  private static readonly GET_ALL_UNMAPPED_RECORD = environment.apiV1 + '/all-unmapped-record';
  private static readonly UPDATE_MAP_NAME = environment.apiV1 + '/map-name';
  private static readonly GET_MASTER_MAP_SHEET_ON_ID = environment.apiV1 + '/master-map-sheet-on-id';
  private static readonly GET_MAPS_UNGROUP_MAPPING = environment.apiV1 + '/maps-ungroup-mapping';
  private static readonly LEAD_SHEET = environment.apiV1 + '/lead-sheet';
  private static readonly LEAD_SHEET_NAME = environment.apiV1 + '/lead-sheet-name';
  private static readonly MAP_NO = environment.apiV1 + '/map-no';
  private static readonly GET_LEAD_SHEET_NEXT_CODE = environment.apiV1 + '/lead-sheet-next-code';
  private static readonly UPDATE_AUDITOR_STATUS = environment.apiV1 + '/tb/auditor-status-update';
  private static readonly UPDATE_STATEMENT_VIEW_TYPE = environment.apiV1 + '/statement-view-type';
  private static readonly GET_MAP_SUB_GROUP = environment.apiV1 + '/map-sub-group';
  private static readonly GET_MAP_GROUP = environment.apiV1 + '/map-group';
  private static readonly SAVE_MAPPING_SHEET = environment.apiV1 + '/mapping-sheet';
  private static readonly GET_MAPPED_MASTER_MAP_SHEET = environment.apiV1 + '/mapped-master-map-sheet';
  private static readonly GET_ADJUSTMENT_UUIDS = environment.apiV1 + '/tb/journal/adj-uuids';
  private static readonly LEAD_SHEET_GROUPING = environment.apiV1 + '/tb/lead-sheet-grouping';
  private static readonly SAVE_NEW_TB_MAPPING = environment.apiV1 + '/new-tb-mapping';
  private static readonly REMOVE_TB = environment.apiV1 + '/tb/delete';
  private static readonly TB_ROW_UNASSIGN_ALL = environment.apiV1 + '/row-unassign-all';
  private static readonly TB_ROW_UNMAP_ALL = environment.apiV1 + '/row-unmap-all';
  private static readonly GET_US_TAX_CODE_EXPORT = environment.apiV1 + '/us-tax-code-export';
  private static readonly GET_ALL_GIFI = environment.apiV1 + '/tb/all-gifi';
  private static readonly GIFI_CODE = environment.apiV1 + '/tb/gifi-code';
  private static readonly GET_TB_FILTER = environment.apiV1 + '/tb/filter';
  private static readonly GET_FX_SETTING_DATA = environment.apiV1 + '/tb/fx-settings-data';
  private static readonly SAVE_TB_FX_RATE = environment.apiV1 + '/tb/fx-tb-rate';
  private static readonly CLEAR_ALL_FX_DATA = environment.apiV1 + '/tb/clear-all-fx-data';
  private static readonly SAVE_FX_RATE = environment.apiV1 + '/tb/fx-rate';
  private static readonly SAVE_ORIGINAL_BALANCE_EDIT = environment.apiV1 + '/tb/original-balance-edit';
  private static readonly AUTO_MAP_TB = environment.apiV1 + '/tb/auto-map-tb';
  private static readonly UNASSIGN_TB_SINGLE_ROW = environment.apiV1 + '/tb/unassign-tb-single-row';


  constructor(private http: HttpClient) {}

  refreshTrialBalance(engagementId: number): Observable<boolean> {
    return this.http.post<boolean>(TbApiService.tbBaseUri + '/refresh', engagementId, {responseType: 'json'});
  }

  getTrialBalance(engagementId: number, year: number): Observable<GenericResModel<TrialBalanceModel[]>> {
    return this.http.post<GenericResModel<TrialBalanceModel[]>>(TbApiService.tbBaseUri + '/get', {
      'id': engagementId,
      'year': year
    });
  }

  createAccount(data: TbAccountReqModel): Observable<TbAccountResModel> {
    return this.http.post<TbAccountResModel>(TbApiService.tbBaseUri + '/account/add', data, {responseType: 'json'});
  }

  validateCode(data: TbAccountReqModel): Observable<TbAccountResModel> {
    return this.http.post<TbAccountResModel>(TbApiService.tbBaseUri + '/account/validate-code', data);
  }

  validateName(data: TbAccountReqModel): Observable<TbAccountResModel> {
    return this.http.post<TbAccountResModel>(TbApiService.tbBaseUri + '/account/validate-name', data, {responseType: 'json'});
  }

  pushJournalEntry(entry: JournalReqModel): Observable<GenericResModel<JournalResModel>> {
    return this.http.put<GenericResModel<JournalResModel>>(TbApiService.tbBaseUri + '/journal/add-entry', entry, {responseType: 'json'});
  }

  validateEntry(entry: JournalReqModel): Observable<GenericResModel<JournalResModel>> {
    return this.http.post<GenericResModel<JournalResModel>>(TbApiService.tbBaseUri + '/journal/validate-entry', entry, {responseType: 'json'});
  }

  importTb(data: any): Observable<any> {
    return this.http.post(TbApiService.tbBaseUri + '/import', data, {responseType: 'json'});
  }

  getProcedureNotes(data: ProceduresNotesReqModel): Observable<GenericResModel<ProceduresNotesModel>> {
    return this.http.post<GenericResModel<ProceduresNotesModel>>(environment.apiV1 + '/procedures/notes/get', data);
  }

  getLastUpdatedTbInfo(engagementId: number): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + `/tb/refresh-timestamp/${engagementId}`);
  }

  saveMultiMapping(data: any) {
    return this.http.put<GenericResModel<JournalResModel>>(TbApiService.multiMapping, data, {responseType: 'json'});
  }

  getLeadSheetCode(engagementId: number, year: number): Observable<any> {
    return this.http.get(environment.apiV1 + TbApiService.leadSheetCode + engagementId + '/' + year);
  }

  getMasterMapSheet(inList: string): Observable<any> {
    return this.http.get(environment.apiV1 + '/master-map-sheet', {params: {'inList': inList}});
  }

  getTrialBalanceSignoff(engId: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/trial-balance-signoff', {params: {'engId': engId}});
  }

  getGifiCode(engId: number, acctYear: number): Observable<any> {
    return this.http.get(TbApiService.GIFI_CODE, {params: {'engId': engId, 'acctYear': acctYear}});
  }

  saveGifiCode(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.GIFI_CODE, data);
  }

  getMapNo(engId: number, acctYear: number): Observable<any> {
    return this.http.get(TbApiService.MAP_NO, {params: {'engId': engId, 'acctYear': acctYear}});
  }

  removeMapNo(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(TbApiService.MAP_NO, {params: {'data': data}});
  }

  getGroupMasterMapSheet(engId: number): Observable<any> {
    return this.http.get(environment.apiV1 + '/group-master/map-sheet', {params: {'engId': engId}});
  }

  getShortcutButton(engId: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.SHORT_CUT_BUTTON, {params: {'engId': engId}});
  }

  getTrialBalanceAccount(engId: number, endYear: number): Observable<any> {
    return this.http.get(TbApiService.TB_ACCOUNT, {params: {'engId': engId, 'endYear': endYear}});
  }

  unGroup(ungroupModel: UngroupModel): Observable<any> {
    return this.http.put(TbApiService.UNGROUP, ungroupModel);
  }

  unMap(data: string): Observable<any> {
    return this.http.put(TbApiService.UNMAP, data);
  }

  getAllUnmapRecord(engId: number, year: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_ALL_UNMAPPED_RECORD, {
      params: {'engId': engId, 'year': year}
    });
  }

  updateMapName(data: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(TbApiService.UPDATE_MAP_NAME, data);
  }

  getMasterMapSheetOnId(masterMapSheetId: number): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_MASTER_MAP_SHEET_ON_ID, {
      params: {'masterMapSheetId': masterMapSheetId}
    });
  }

  getMapsUngroupMapping(finSubGroupId: number, engId: number): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_MAPS_UNGROUP_MAPPING, {
      params: {
        'finSubGroupId': finSubGroupId,
        'engId': engId
      }
    });
  }

  getLeadSheet(engId: number, searchTxt: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.LEAD_SHEET, {
      params: {
        'engId': engId,
        'searchTxt': searchTxt
      }
    });
  }

  saveLeadSheet(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.LEAD_SHEET, data);
  }

  getLeadSheetNextCode(finSubGroupId: number, engId: number, isEdit: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_LEAD_SHEET_NEXT_CODE, {
      params: {
        'finSubGroupId': finSubGroupId,
        'engId': engId,
        'isEdit': isEdit
      }
    });
  }

  removeLeadSheetName(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(TbApiService.LEAD_SHEET_NAME, {params: {'data': data}});
  }

  updateLeadSheetName(data: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(TbApiService.LEAD_SHEET_NAME, data);
  }

  updateTbAuditorStatus(engagementId: number, auditorStatus: number, date: string): Observable<GenericResModel<string>> {
    let params = new HttpParams();
    if (engagementId) {
      params = params.set('engagementId', engagementId);
    }
    if (auditorStatus) {
      params = params.set('auditorStatus', auditorStatus);
    }
    if (date) {
      params = params.set('date', date);
    }
    return this.http.put<GenericResModel<string>>(TbApiService.UPDATE_AUDITOR_STATUS, {}, {params});
  }

  updateStatementViewType(statementViewTypeModel: StatementViewTypeModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(TbApiService.UPDATE_STATEMENT_VIEW_TYPE, statementViewTypeModel);
  }

  getMapSubGroup(finSubGroupId: number): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_MAP_SUB_GROUP, {
      params: {'finSubGroupId': finSubGroupId}
    });
  }

  getMapGroup(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_MAP_GROUP);
  }

  saveMappingSheet(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.SAVE_MAPPING_SHEET, data);
  }

  getMappedMasterMapSheet(engId: number, searchIdx: string, year: string, data: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_MAPPED_MASTER_MAP_SHEET,
      {
        params: {
          'engId': engId,
          'searchIdx': searchIdx,
          'year': year,
          'data': data
        }
      });
  }

  getAdjEntryUuids(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_ADJUSTMENT_UUIDS);
  }

  getLeadSheetGrouping(data: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.LEAD_SHEET_GROUPING, {params: {'data': data}});
  }

  saveLeadSheetGrouping(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.LEAD_SHEET_GROUPING, data);
  }

  saveNewTbMapping(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.SAVE_NEW_TB_MAPPING, data);
  }

  deleteTB(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(TbApiService.REMOVE_TB, {params: {data: data}});
  }

  unassignAllTbRow(data: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(TbApiService.TB_ROW_UNASSIGN_ALL, data);
  }

  unmapAllTbRow(data: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(TbApiService.TB_ROW_UNMAP_ALL, data);
  }

  getUsTaxCodeExport(data: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_US_TAX_CODE_EXPORT, {params: {'data': data}});
  }

  getCountOfTbAccDetails(data: deleteTbAccReqModel): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(this.TB_ACC_DETAILS_COUNT, data, {responseType: 'json'});
  }

  deleteTbAccounts(data: deleteTbAccReqModel): Observable<GenericResModel<any>> {
    return this.http.delete<GenericResModel<any>>(this.TB_ACC_DETAILS_COUNT, {body: data});
  }

  editAccount(data: editRequestModel): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(TbApiService.tbBaseUri + this.TB_EDIT_ACC, data, {responseType: 'json'});
  }

  getZeroAccounts(engId: number, year: number): Observable<GenericResModel<ZeroAccountsModel>> {
    return this.http.get<GenericResModel<ZeroAccountsModel>>(environment.apiV1 + '/zero-dollar-acc', {
      params: {'engagementsId': engId, 'year': year}
      });
  }

  getZeroAccountsCount(engId: number, year: number): Observable<GenericResModel<ZeroAccountsModel>> {
    return this.http.get<GenericResModel<ZeroAccountsModel>>(environment.apiV1 + '/zero-dollar-count', {
      params: {'engagementId': engId, 'year': year}
      });
  }

  importZeroAccountsCount(engId: number, year: number, accounts: ZeroAccounts[]): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(environment.apiV1 + '/zero-dollar-acc', accounts , {
      params: {'engagementId': engId, 'year': year}
      });
  }

  setZeroAccHide(engId: number, isZeroAccHide: boolean): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(environment.apiV1 + '/eng/' + engId + '/zero-dollar-acc?isHide=' + isZeroAccHide, null);
  }

  getAllGifi(data: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_ALL_GIFI, {params: {data: data}});
  }

  getTbFilter(data: string): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(TbApiService.GET_TB_FILTER, data);
  }

  getFxSettingsData(data: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(TbApiService.GET_FX_SETTING_DATA, {params: {data: data}});
  }

  saveTbFxRate(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.SAVE_TB_FX_RATE, data);
  }

  clearAllFxData(data: string): Observable<GenericResModel<string>> {
    return this.http.delete<GenericResModel<string>>(TbApiService.CLEAR_ALL_FX_DATA, {params: {data: data}});
  }

  saveFxRate(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.SAVE_FX_RATE, data);
  }

  saveOriginalBalanceEdit(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.SAVE_ORIGINAL_BALANCE_EDIT, data);
  }

  autoMapTb(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.AUTO_MAP_TB, data);
  }

  unassignTbSingleRow(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(TbApiService.UNASSIGN_TB_SINGLE_ROW, data);
  }

  validateAccNoDesc(data: editRequestModel): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(TbApiService.TB_VALIDATE_ACCNO_DESC, data, {responseType: 'json'});
  }
}
